import React, { useMemo, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { populateArtifact } from "services/DataService";
import WorkflowRunSearchCardComponent from "../WorkflowRunSearchCard";
import { HeaderWithStats } from "services/MetadataServices";
import { ScheduledJobProto } from "build-ts/schedule";
import useRunAuthorNames from "hooks/useRunAuthorNames";

interface WorkflowRunSearchCardContainerProps {
  artifact: ArtifactProto;
  defaultTimeZone?: string;
  /** Called with the fully-populated WorkflowRun artifact after a successful fetch. */
  onOpenWorkflowRun?: (artifact: ArtifactProto) => void;
  analystList?: HeaderWithStats[];
  scenarioJobs?: ScheduledJobProto[];
}

/**
 * Container for WorkflowRunSearch artifacts.
 * Owns the per-row loading state and the populateArtifact fetch so the
 * pure component stays free of async logic.
 */
const WorkflowRunSearchCardContainer: React.FC<WorkflowRunSearchCardContainerProps> = ({
  artifact,
  defaultTimeZone,
  onOpenWorkflowRun,
  analystList,
  scenarioJobs,
}) => {
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);

  // Build run refs (runId and sourceChatId) so the hook can resolve each run's author name from its source chat.
  const runRefs = useMemo(
    () =>
      (artifact.workflow_run_search?.workflow_runs ?? [])
        .filter(run => !!run.header?.id && !!run.source_chat_id)
        .map(run => ({ runId: run.header!.id!, sourceChatId: run.source_chat_id! })),
    [artifact.workflow_run_search?.workflow_runs]
  );

  const runAuthorNames = useRunAuthorNames(runRefs);

  const handleRunClick = async (runId: string) => {
    if (loadingRunId) return;
    setLoadingRunId(runId);
    try {
      const request = ArtifactProto.create({
        artifact_type: ArtifactType.WORKFLOW_RUN,
        workflow_run: artifact.workflow_run_search?.workflow_runs?.find(run => run.header?.id === runId),
      });
      const populated = await populateArtifact(request);
      if (populated) {
        onOpenWorkflowRun?.(populated);
      }
    } finally {
      setLoadingRunId(null);
    }
  };

  return (
    <WorkflowRunSearchCardComponent
      artifact={artifact}
      defaultTimeZone={defaultTimeZone}
      loadingRunId={loadingRunId}
      onRunClick={onOpenWorkflowRun ? handleRunClick : undefined}
      analystList={analystList}
      scenarioJobs={scenarioJobs}
      runAuthorNames={runAuthorNames}
    />
  );
};

export default WorkflowRunSearchCardContainer;
